<template>
  <v-card>
    <v-card-title>
      <v-row no-gutters="">
        <v-col class="d-flex align-center">
          {{ t('ipsums') }}
        </v-col>
        <v-col cols="auto" align-self="end">
          <v-switch
            color="primary"
            density="compact"
            :label="t('enable_feature')"
            v-model="enableFeature"
            hide-details
          />
        </v-col>
      </v-row>
    </v-card-title>
    <v-form @submit.prevent="submit">
      <v-expand-transition>
        <v-card-text v-show="enableFeature" class="pt-0">
          <v-autocomplete
            :label="t('linked_ipsums')"
            multiple
            chips
            clearable
            v-model="form.linked_ipsums.value.value"
            :error-messages="form.linked_ipsums.errorMessage.value"
            :items="ipsumDevices"
            item-title="visual_name"
            item-value="id"
            density="comfortable"
          />
          <DurationPicker
            density="compact"
            v-model="form.postponed_send_timing.value.value"
            show-enable
          >
            <v-row no-gutters>
              <v-col cols="auto">
                {{ t('manual_push_data') }}
              </v-col>
              <v-col class="ms-1 d-flex">
                <v-tooltip :text="t('tooltip_manual_push')">
                  <template v-slot:activator="{ props }">
                    <v-icon v-bind="props" icon="mdi-help-circle" size="x-small"/>
                  </template>
                </v-tooltip>
              </v-col>
            </v-row>
          </DurationPicker>
          <v-row>
            <v-col>
              <v-text-field
                :label="t('ipsum_formula_1')"
                density="compact"
                v-model="form.avalpower_formula_1.value.value"
                :error-messages="form.avalpower_formula_1.errorMessage.value"
              />
            </v-col>
            <v-col>
              <v-text-field
                :label="t('ipsum_formula_2')"
                density="compact"
                v-model="form.avalpower_formula_2.value.value"
                :error-messages="form.avalpower_formula_2.errorMessage.value"
              />
            </v-col>
            <v-col>
              <v-text-field
                :label="t('ipsum_formula_3')"
                density="compact"
                v-model="form.avalpower_formula_3.value.value"
                :error-messages="form.avalpower_formula_3.errorMessage.value"
              />
            </v-col>
          </v-row>
          <v-card variant="tonal">
            <v-card-title>
              <v-row no-gutters>
                <v-col>
                  {{ t('ipsum_last_data') }}
                </v-col>
                <v-col cols="auto">
                  <v-fade-transition>
                    <v-progress-circular v-show="lastDataLoading" size="28" indeterminate/>
                  </v-fade-transition>
                </v-col>
              </v-row>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="4" v-for="ipsum in lastIpsumData">
                  <v-card v-if="ipsum['powers'] !== undefined" class="fill-height">
                    <v-card-title class="pb-0 text-center"><b>D{{ ipsum.id }}</b></v-card-title>
                    <v-card-text class="text-center">
                      <p><b>{{ t('last_data_at') }}:</b></p>
                      <p>{{ toPrettyDatetime(ipsum.timestamp) }}</p>
                      <br/>
                      <p><b>{{ t('data') }}</b></p>
                      <p v-for="(power, idx) in ipsum['powers']">
                        <b>D{{ ipsum.id }}P{{ idx + 1 }}</b>: {{ power }}
                      </p>
                      <br/>
                      <p><b>{{ t('note') }}:</b></p>
                      <p>{{ ipsum.note }}</p>
                    </v-card-text>
                  </v-card>
                  <v-card v-else class="fill-height">
                    <v-card-title class="pb-0 text-center"><b>D{{ ipsum.id }}</b></v-card-title>
                    <v-card-text class="text-center">
                      <p><b>{{ t('last_data_at') }}:</b><br/>{{ t('no_data') }}</p>
                      <br/>
                      <p><b>{{ t('note') }}:</b></p>
                      <p>{{ t('insert_note_here') }}</p>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-expand-transition>
      <v-card-actions class="pt-0 justify-end">
        <v-btn border type="submit">{{ t('save') }}</v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script setup>
import { ref, toRefs, watch, onMounted, onUnmounted } from 'vue';
import { useForm, useFieldValue } from 'vee-validate';
import * as yup from 'yup';
import DurationPicker from "../DurationPicker.vue";
import {
  getFormFromSchema,
  setObjectIntoFormBySchema,
  toPrettyDatetime
} from "../../utils.js";
import {
  createParkIpsumConfig,
  deleteParkIpsumConfig,
  fetchParkIpsumConf,
  modifyPutParkIpsumConfig
} from "../../remote/parkipsumconf.js";
import { fetchIpsumLastPowerDefault } from "../../remote/ipsum.js";
import { useI18n } from "vue-i18n";

const { t } = useI18n({ inheritLocale: true, useScope: 'local' });
// General State
const useAlerts = defineModel('useAlerts');
const loadingOverlay = defineModel('loadingOverlay');
const props = defineProps(['originalPark', 'ipsumDevices']);
const { originalPark, ipsumDevices } = toRefs(props);
const emit = defineEmits(['updated']);

// Internal State
const enableFeature = ref(false);
const originalEnable = ref(false);
const lastDataInterval = ref(null);
const lastIpsumData = ref([]);
const lastDataLoading = ref(false);

// FORM
const schema = yup.object({
  linked_ipsums: yup.array().of(yup.number().positive()).min(1),
  avalpower_formula_1: yup.string().required(),
  avalpower_formula_2: yup.string().optional(),
  avalpower_formula_3: yup.string().optional(),
  postponed_send_timing: yup.string().nullable(),
});

// noinspection JSCheckFunctionSignatures
const { handleSubmit } = useForm({
  validationSchema: schema
});

const form = getFormFromSchema(schema);

async function updateIpsumLastData() {
  lastDataLoading.value = true;
  let futures = [];
  let ids = [];
  for (const ipsumId of form.linked_ipsums.value['value']) {
    futures.push(fetchIpsumLastPowerDefault(ipsumId));
    ids.push(ipsumId);  // I'm not trusting linked_ipsum anymore after await because it can be changed in the meantime!
  }
  let data = await Promise.all(futures).catch((e) => {
    useAlerts.value.errorAlert("Unexpected error during Ipsum Last Power retrieve", `${JSON.stringify(e.request)}`);
  });
  for (const i in ids) data[i]['id'] = ids[i];
  for (const i in ids) data[i]['note'] = ipsumDevices.value.find(el => el['id'] === ids[i])?.note || '';
  lastIpsumData.value = data;
  lastDataLoading.value = false;
}

// noinspection JSCheckFunctionSignatures
const linkedIpsumValue = useFieldValue('linked_ipsums');

watch(linkedIpsumValue, updateIpsumLastData);

async function loadData(parkData) {
  if (parkData !== null) {
    // If a park is selected...
    const parkIpsumConf = await fetchParkIpsumConf(parkData.id);
    if (parkIpsumConf !== null) {
      // And the config exists, set it as already enabled
      setObjectIntoFormBySchema(parkIpsumConf, form, schema);
      enableFeature.value = true;
      originalEnable.value = true;
      if (lastDataInterval.value === null) lastDataInterval.value = setInterval(updateIpsumLastData, 5000);
      return;
    }
  }
  // If Park is null OR the config DOESN'T exist, disable everything
  enableFeature.value = false;
  originalEnable.value = false;
  if (lastDataInterval.value !== null) {
    clearInterval(lastDataInterval.value);
    lastDataInterval.value = null;
  }
}

// In theory the watch is enough because for now impossible to have the Component loaded with already a park onMounted.
watch(originalPark, loadData);

// But JUST TO BE SURE, let's make a onMounted too, that just get skipped if the prop is null
onMounted(async () => {
  await loadData(originalPark.value);
});

onUnmounted(() => {
  clearInterval(lastDataInterval.value);
  lastDataInterval.value = null;
});

// noinspection JSValidateTypes
const submitForm = handleSubmit(async (values) => {
  // castEmptyToNullFromObj(values);
  values['park'] = originalPark.value.id;
  console.log(values);
  loadingOverlay.value = true;
  if (originalEnable.value) {
    // Update the ParkIpsumConf
    try {
      await modifyPutParkIpsumConfig(useAlerts, values, originalPark.value.id);
      emit('updated');
    } catch (e) {
      useAlerts.value.errorAlert('Error during Park Ipsum Configuration edit', e.toString());
    } finally {
      loadingOverlay.value = false;
    }
  } else {
    // Create the ParkIpsumConf
    try {
      await createParkIpsumConfig(useAlerts, values);
      emit('updated');
    } catch (e) {
      useAlerts.value.errorAlert('Error during Park Ipsum Configuration creation', e.toString());
    } finally {
      loadingOverlay.value = false;
    }
  }
});

const submit = async (evt) => {
  if (enableFeature.value) {
    // Payload creation
    await submitForm(evt);
  } else {
    if (originalEnable.value) {
      // Delete the ParkIpsumConf
      try {
        await deleteParkIpsumConfig(useAlerts, originalPark.value.id);
        emit('updated');
      } catch (e) {

      }
    } else {
      console.log("ParkIpsumConf saved disabled on disabled, nothing done.");
    }
  }
};

</script>

<i18n>
{
  "en": {
    "ipsums": "Ipsums",
    "enable_feature": "Enable Feature",
    "linked_ipsums": "Linked Ipsums",
    "manual_push_data": "Manual Push Data",
    "tooltip_manual_push": "If enabled, data will be sent only upon manual request. If disabled, it will be sent automatically at the end of the day.",
    "ipsum_formula_1": "Ipsum Formula 1",
    "ipsum_formula_2": "Ipsum Formula 2",
    "ipsum_formula_3": "Ipsum Formula 3",
    "ipsum_last_data": "Ipsum Last Data",
    "last_data_at": "Last data at",
    "data": "Data",
    "note": "Note",
    "no_data": "No data",
    "insert_note_here": "Insert note here",
    "save": "Save"
  },
  "it": {
    "ipsums": "Ipsum",
    "enable_feature": "Abilita Funzionalità",
    "linked_ipsums": "Ipsum Collegati",
    "manual_push_data": "Invio Dati Manuale",
    "tooltip_manual_push": "Se abilitato, i dati verranno inviati solo su richiesta manuale. Se disabilitato, verranno inviati automaticamente alla fine della giornata.",
    "ipsum_formula_1": "Formula Ipsum 1",
    "ipsum_formula_2": "Formula Ipsum 2",
    "ipsum_formula_3": "Formula Ipsum 3",
    "ipsum_last_data": "Ultimi Dati Ipsum",
    "last_data_at": "Ultimo dato alle",
    "data": "Dati",
    "note": "Nota",
    "no_data": "Nessun dato",
    "insert_note_here": "Inserire nota qui",
    "save": "Salva"
  },
  "fr": {
    "ipsums": "Ipsums",
    "enable_feature": "Activer la Fonctionnalité",
    "linked_ipsums": "Ipsums Liés",
    "manual_push_data": "Envoi Manuel des Données",
    "tooltip_manual_push": "Si activé, les données ne seront envoyées que sur demande manuelle. Si désactivé, elles seront envoyées automatiquement à la fin de la journée.",
    "ipsum_formula_1": "Formule Ipsum 1",
    "ipsum_formula_2": "Formule Ipsum 2",
    "ipsum_formula_3": "Formule Ipsum 3",
    "ipsum_last_data": "Dernières Données Ipsum",
    "last_data_at": "Dernières données à",
    "data": "Données",
    "note": "Note",
    "no_data": "Pas de données",
    "insert_note_here": "Insérer la note ici",
    "save": "Enregistrer"
  }
}
</i18n>

<style scoped>

</style>
